import { get, set, isEqual } from "lodash-es";
import Cookies from "js-cookie";
import { Location } from "history";
import { getResponse, getErrors, qs } from "@stemscopes/api";
import { endpoints, routes } from "configs";
import { request } from "utils";
import { unregister } from "serviceWorkerRegistration";
import { activateServiceWorker } from "components/offline-tools/duck/operations";
import * as Types from "./types";
import { setIsOnline } from "../../../configs/store/offline-mode/actions";

const updateHtmlClassName: Types.UpdateHtmlClassName = (
  prefix,
  currentClassName,
  previousClassName,
) => {
  if (currentClassName && currentClassName !== "default") {
    document.documentElement.classList.add(`${prefix}-${currentClassName}`);
  }

  if (previousClassName && previousClassName !== currentClassName) {
    document.documentElement.classList.remove(`${prefix}-${previousClassName}`);
  }
};

export const updateSettings: Types.UpdateSettings = (user, prevUser) => {
  if (!user) {
    return;
  }

  updateHtmlClassName("theme", user.accTheme, prevUser?.accTheme);
  updateHtmlClassName("font-type", user.accTypeFace, prevUser?.accTypeFace);
  updateHtmlClassName("font-size", user.accTypeSize, prevUser?.accTypeSize);
};

export const authenticationCheck: Types.AuthenticateCheck = (
  setAuth,
  setPin,
  dispatch,
  navigate,
  online,
) => {
  if (window.location.pathname !== routes.externalSessionAuthenticate) {
    request
      .get(endpoints.authenticationCheck, {
        headers: {
          "Cache-Control": "no-cache",
        },
      })
      .then((response) => {
        const { data } = response.data;
        setPin(data.user.offlineMode.pinCode);
        setAuth({
          isLoggedIn: true,
          isAuthenticating: false,
          data,
        });

        if (data.school.offlineMode) {
          localStorage.setItem(
            "deviceUniqId",
            data.user.offlineMode.deviceUniqId,
          );
        }

        if (data.user.offlineMode.access) {
          activateServiceWorker(dispatch, data);
        } else {
          unregister();
        }
      })
      .catch((err) => {
        if (!err) {
          dispatch(setIsOnline(false));
        }
        setAuth({
          isLoggedIn: false,
          isAuthenticating: false,
          data: {} as any,
        });
        if (!online) {
          navigate(routes.offlineLogin);
        }
        localStorage.removeItem("token");
        localStorage.removeItem("globalId");
        localStorage.removeItem("deviceUniqId");
      });
  } else {
    localStorage.removeItem("token");
    setAuth({ isAuthenticating: false });
  }
};

export const prepareRequest: Types.PrepareRequest = (req) => {
  const deviceUniqId = localStorage.getItem("deviceUniqId");
  req.headers.Authorization = localStorage.getItem("token");

  if (deviceUniqId && deviceUniqId !== "null") {
    req.headers["Device-Uniq-ID"] = deviceUniqId;
  }

  if (!get(req, "params.page")) {
    set(req, "params.page", { number: 1, size: 10000 });
  }

  if (
    localStorage.getItem("globalId") &&
    req.method !== "post" &&
    req.method !== "patch" &&
    req.method !== "put"
  ) {
    req.url = `${req.url}${
      req.url.indexOf("?") > 0 ? "&" : "?"
    }gid=${localStorage.getItem("globalId")}`;
  }

  return req;
};

export const clearAppLocalStorage = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("globalId");
  localStorage.removeItem("deviceUniqId");
  localStorage.removeItem("newTeacherInfo");
  localStorage.removeItem("hasDeletedAssignmentsFromCache");
};

export const prepareSuccess: Types.PrepareSuccess = (response) => {
  switch (response.status) {
    case 200:
    case 201:
    case 204: {
      return Promise.resolve(
        getResponse({ response, params: response.config.params }),
      );
    }
    default:
      return Promise.reject(response);
  }
};

export const prepareFailure: Types.PrepareFailure = ({
  error,
  navigate,
  setAuth,
}) => {
  const { response } = error;

  switch (response?.status) {
    case 307: {
      if (Cookies.get("stemscopes-version") === "3.5") {
        Cookies.set("stemscopes-version", "2.0", {
          domain: ".acceleratelearning.com",
        });
        unregister(false);
        window.location.replace(response.data?.data?.redirectTo);
      }

      return Promise.reject(response);
    }
    case 400:
    case 422:
      return Promise.reject(getErrors(response));
    case 401:
      clearAppLocalStorage();

      if (
        ![routes.externalSessionAuthenticate, routes.lti.error].includes(
          window.location.pathname,
        )
      ) {
        const hasKey = window.location.search.indexOf("key") > 0;
        navigate("/", { state: hasKey ? window.location : {} });
      }
      setAuth({ isLoggedIn: false, data: {} as any });
      return Promise.reject(response);
    default:
      return Promise.reject(response);
  }
};

const comparePathAndHash = (
  prevLocation: Location<unknown>,
  currLocation: Location<unknown>,
): boolean =>
  `${prevLocation.pathname}${prevLocation.hash}` ===
  `${currLocation.pathname}${currLocation.hash}`;

const compareQueryString = (prevQs: string, currQs: string): boolean => {
  const prev = qs.parse(prevQs.slice(1));
  const curr = qs.parse(currQs.slice(1));

  return isEqual(prev, curr);
};

export const arePathsEqual = (
  prevLocation: Location<unknown>,
  currLocation: Location<unknown>,
): boolean =>
  comparePathAndHash(prevLocation, currLocation) &&
  compareQueryString(prevLocation.search, currLocation.search);
